import {
  type EditorPlugin,
  type DecorateNodeCallback,
} from '../../types/aha-editor';
import TranscriptPromptComponent from './components/TranscriptPromptComponent';
import TranscriptSpeakerComponent from './components/TranscriptSpeakerComponent';
import TranscriptCueComponent from './components/TranscriptCueComponent';
import BlockFormatPluginFactory from '../Blocks/BlockFormatPluginFactory';
import { Editor, Element, type Path } from 'slate';
import { ReactEditor } from 'slate-react';
import AhaEditor from '../../AhaEditor';
import AhaEditorUtils from '../../AhaEditorUtils/AhaEditorUtils';
import TranscriptCueSchemaPlugin from './TranscriptCueSchemaPlugin';

const transcriptHighlightCue = (editor: Editor, cuePath: Path | null): void => {
  const ahaEditor = AhaEditor.getForEditor(editor);
  if (ahaEditor) {
    const transcriptState = ahaEditor.controller.state.pluginState.transcript;
    if (transcriptState) {
      transcriptState.currentCuePath = cuePath;

      // Force the decorations to update.
      AhaEditorUtils.Rendering.forceComputeDecorations(editor);
    }
  }
};

function highlightCue(
  editor: Editor,
  timeInSeconds: number
): HTMLElement | undefined {
  // Find a transcript-cue that contains the given time.
  const [cueEntry] = Editor.nodes(editor, {
    at: [],
    match: (node) => {
      if (Element.isElement(node) && node.type === 'transcript-cue') {
        return (
          node.cue_startTime <= timeInSeconds &&
          node.cue_endTime >= timeInSeconds
        );
      }
      return false;
    },
    mode: 'lowest',
  });
  if (cueEntry) {
    const [, cuePath] = cueEntry;

    transcriptHighlightCue(editor, cuePath);

    const cueRange = Editor.range(editor, cuePath);
    const domRange = ReactEditor.toDOMRange(editor, cueRange);
    const leafEl = domRange.startContainer.parentElement!;

    return leafEl;
  } else {
    transcriptHighlightCue(editor, null);
  }
}

interface TranscriptPluginAPI {
  highlightCue: typeof highlightCue;
}

// For transcripts we use three components, one for the prompt, one for the speaker
// and one for the cue.
// This allows us to collapse many cues from the same speaker into a single block.
// Using an Element for the cues (rather than a mark) allows better control
// over the rendering and better behavior when cues are moved around a document.

const TranscriptPromptComponentPlugin: EditorPlugin = {
  name: 'Transcript Prompt Component Plugin',
  component: {
    type: 'transcript-prompt',
    component: TranscriptPromptComponent,
  },
};

const TranscriptPromptBlockPlugin = BlockFormatPluginFactory({
  type: 'transcript-prompt',
  title: 'Transcript Prompt',
  isMultiline: true, // Allows arrowing out of the block.
  allowTab: false,
  matchIndent: false,
  hasCustomComponent: true,
  dependencies: [TranscriptPromptComponentPlugin],
});

const TranscriptSpeakerComponentPlugin: EditorPlugin = {
  name: 'Transcript Speaker Component Plugin',
  component: {
    type: 'transcript-speaker',
    component: TranscriptSpeakerComponent,
  },
};

const TranscriptSpeakerBlockPlugin = BlockFormatPluginFactory({
  type: 'transcript-speaker',
  title: 'Transcript Speaker',
  isMultiline: true, // Allows arrowing out of the block.
  allowTab: true,
  matchIndent: false,
  hasCustomComponent: true,
  exitOnBackspace: true,
  dependencies: [TranscriptSpeakerComponentPlugin],
});

const TranscriptCuePlugin: EditorPlugin = {
  name: 'Transcript Cue Component Plugin',
  component: {
    type: 'transcript-cue',
    component: TranscriptCueComponent,
  },
};

const TranscriptApiPlugin: EditorPlugin<TranscriptPluginAPI> = {
  name: 'Transcript API Plugin',
  api: {
    highlightCue,
  },
};

// Plugin to highlight the current cue being played in the video.
const TranscriptCurrentCuePlugin: EditorPlugin = {
  name: 'Current Cue Plugin',
  decorator: {
    decorate: (editor: Editor, decorateNode: DecorateNodeCallback): void => {
      const ahaEditor = AhaEditor.getForEditor(editor)!;
      const cuePath =
        ahaEditor.controller?.state?.pluginState?.transcript?.currentCuePath;

      if (cuePath) {
        const [cueNode] = Editor.node(editor, cuePath);
        const range = Editor.range(editor, cuePath);
        decorateNode(cueNode, range, {
          classNames: {
            [AhaEditorUtils.Decorations.decorationClass(
              'aha-editor-field-v2__currentTranscriptCue'
            )]: true,
          },
        });
      }
    },
  },
  setPluginState: (pluginState: any): void => {
    pluginState.transcript = { currentCuePath: null };
  },
};

const TranscriptPlugin: EditorPlugin[] = [
  TranscriptCueSchemaPlugin,
  TranscriptPromptBlockPlugin,
  TranscriptSpeakerBlockPlugin,
  TranscriptCuePlugin,
  TranscriptCurrentCuePlugin,
];

export default TranscriptPlugin;
export { TranscriptApiPlugin };
